import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { ErrorResponse, type User, type UserManager } from "oidc-client-ts";
import { IdentityContext, type Identity } from "./identity";
import { IDP_STORAGE_KEY, type CognitoFrontConfig } from "./idpSession";
import { cognitoLogoutUrl, cognitoUserFromProfile } from "./cognitoUser";

/** Renueva antes de que venza: un request que sale con pocos segundos de vida llega vencido. */
const MIN_TOKEN_LIFETIME_S = 60;

const loginRequired = () => Object.assign(new Error("login_required"), { error: "login_required" });

const vigente = (user: User | null | undefined): user is User =>
  Boolean(user && !user.expired && (user.expires_in ?? 0) > MIN_TOKEN_LIFETIME_S);

/**
 * Cognito (react-oidc-context) con la forma de useAuth0(). El logout revoca el refresh
 * token y cierra la sesión del login hosteado; nunca GlobalSignOut (el token no trae el
 * scope que hace falta para eso, a propósito).
 */
export function CognitoBridge({
  config,
  userManager,
  children,
}: {
  config: CognitoFrontConfig;
  /** El mismo que usa AuthProvider (ver cognitoUserManager.ts). */
  userManager: UserManager;
  children: ReactNode;
}) {
  const auth = useAuth();

  // Al volver a la app pasada la hora del access token, react-oidc-context la da por no
  // autenticada aunque el refresh token (30 días) siga vigente. Auth0 renovaba solo: acá se
  // renueva una vez al cargar, y mientras tanto la app ve "cargando" (no la manda al login).
  const [recovery, setRecovery] = useState<"idle" | "running" | "done">("idle");
  const needsRecovery =
    !auth.isLoading && Boolean(auth.user?.expired) && Boolean(auth.user?.refresh_token);

  // Una sola renovación a la vez por pestaña: oidc-client-ts no comparte la que está en
  // curso, y una pantalla con el token vencido pedía una por request (el 20/10, 58 en un
  // minuto llenaron las conexiones de la base desde el trigger). Renueva con el UserManager
  // y no con auth.signinSilent, que se traga el error y devuelve null: sin el error no se
  // distingue una sesión revocada de un corte de red. Todo va por refs: el interceptor de
  // axios guarda la función del primer render y tiene que ver el usuario de hoy.
  const authRef = useRef(auth);
  authRef.current = auth;
  const renovadoRef = useRef<User | null>(null);
  const enCursoRef = useRef<Promise<User | null> | null>(null);

  const renovar = useCallback((): Promise<User | null> => {
    if (!enCursoRef.current) {
      enCursoRef.current = (async () => {
        const guardado = await userManager.getUser();
        if (!guardado?.refresh_token) return null;
        const user = await userManager.signinSilent();
        renovadoRef.current = user;
        return user;
      })().finally(() => {
        enCursoRef.current = null;
      });
    }
    return enCursoRef.current;
  }, [userManager]);

  // Un minuto antes del vencimiento (la renovación automática de oidc-client-ts está apagada).
  useEffect(() => {
    const alVencer = () => {
      renovar().catch(() => undefined);
    };
    userManager.events.addAccessTokenExpiring(alVencer);
    return () => userManager.events.removeAccessTokenExpiring(alVencer);
  }, [userManager, renovar]);

  useEffect(() => {
    if (!needsRecovery || recovery !== "idle") return;
    setRecovery("running");
    renovar()
      .catch(() => undefined)
      .finally(() => setRecovery("done"));
  }, [needsRecovery, recovery, renovar]);

  const isLoading = auth.isLoading || (needsRecovery && recovery !== "done");

  // login_required sólo para una sesión que ya no sirve (el trucker cierra la sesión con ese
  // error); un corte de red o un error de Cognito es renewal_failed y se reintenta.
  const getAccessTokenSilently = useCallback(async (): Promise<string> => {
    for (const user of [authRef.current.user, renovadoRef.current]) {
      if (vigente(user)) return user.access_token;
    }
    let renewed: User | null;
    try {
      renewed = await renovar();
    } catch (error) {
      if (error instanceof ErrorResponse && error.error === "invalid_grant") throw loginRequired();
      throw Object.assign(new Error("renewal_failed"), { error: "renewal_failed", cause: error });
    }
    if (!renewed) throw loginRequired();
    return renewed.access_token;
  }, [renovar]);

  const loginWithRedirect = useCallback(
    () => auth.signinRedirect({ extraQueryParams: { lang: "es" } }),
    [auth]
  );

  const logout = useCallback(
    async (options?: { logoutParams?: { returnTo?: string } }) => {
      // La app vacía localStorage ANTES de llamar a logout(): el refresh token se toma de
      // memoria (revokeTokens() de oidc-client-ts lo lee del storage y no revocaba nada) y
      // el flag de la sesión se vuelve a guardar para que el próximo login siga en Cognito.
      const refreshToken = auth.user?.refresh_token;
      window.localStorage.setItem(IDP_STORAGE_KEY, "cognito");
      if (refreshToken) {
        try {
          await fetch(`${config.domain}/oauth2/revoke`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({ token: refreshToken, client_id: config.clientId }).toString(),
          });
        } catch {
          // Best effort: igual se cierra la sesión local y la del login hosteado.
        }
      }
      renovadoRef.current = null;
      await auth.removeUser();
      const returnTo = options?.logoutParams?.returnTo ?? window.location.origin;
      window.location.assign(cognitoLogoutUrl(config, returnTo));
    },
    [auth, config]
  );

  const identity = useMemo<Identity>(
    () => ({
      provider: "cognito",
      user: auth.user
        ? cognitoUserFromProfile(auth.user.profile as Record<string, unknown>)
        : undefined,
      isAuthenticated: auth.isAuthenticated,
      isLoading,
      error: auth.error ?? undefined,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
    }),
    [
      auth.user,
      auth.isAuthenticated,
      isLoading,
      auth.error,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
    ]
  );

  return <IdentityContext.Provider value={identity}>{children}</IdentityContext.Provider>;
}
